import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import {
  Box,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  Text,
} from "@skailer-com/uikit";
import { CodeInput } from "@/components/code-input/CodeInput.tsx";
import { sendTokenToExtension } from "@/helpers/send-to-extension.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  useEmployeeTwoFactorSetup,
  useEmployeeTwoFactorSetupVerify,
} from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { ApiTwoFactorSetup } from "@/types/employees/employee.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import * as yup from "yup";

const CODE_VERIFY_DELAY_MS = 300;

type TwoFactorSetupModalProps = ModalProps;

export function TwoFactorSetupModal({ ...props }: TwoFactorSetupModalProps) {
  const { id } = props;
  const { close } = useModals();
  const { showInfo } = useToasts();
  const { t } = useTranslation();
  const twoFactorSetup = useEmployeeTwoFactorSetup();
  const { setToken } = useAuthStore();
  const twoFactorSetupVerify = useEmployeeTwoFactorSetupVerify();
  const [setupData, setSetupData] = useState<ApiTwoFactorSetup | undefined>();
  const [showManual, setShowManual] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const verifyTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
    };
  }, []);

  const connectSchemaObject = yup.object({
    code: yup.string().required(t("errors:field_required")),
  });

  type connectSchema = yup.InferType<typeof connectSchemaObject>;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<connectSchema>({
    mode: "onSubmit",
    resolver: yupResolver(connectSchemaObject),
  });

  useEffectOnce(() => {
    twoFactorSetup.mutateAsync().then((result) => {
      setSetupData(result);
    });
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await twoFactorSetupVerify.mutateAsync(data);

      if (result?.access_token) {
        setToken(result?.access_token);
        sendTokenToExtension(result?.access_token);

        analyticsService.trackEvent(analyticEvents.twoFactorAuthentication.enabled);
      }

      close(id);
      showInfo({
        title: t("core:two_factor_enabled"),
        icon: <FontAwesomeIcon icon={"fa-light fa-check"} />,
      });
    } catch {
      // Errors are handled by the mutation hook
    } finally {
      setIsChecking(false);
    }
  });

  const handleComplete = (fromPaste: boolean) => {
    setIsChecking(true);
    verifyTimerRef.current = setTimeout(
      () => {
        void onSubmit();
      },
      fromPaste ? 0 : CODE_VERIFY_DELAY_MS
    );
  };

  return (
    <Modal withCloser title={t("core:two_factor")} {...props}>
      <Stack gap={"lg"}>
        <Stack gap={"md"}>
          <Stack gap={"xs"}>
            <Text size={"xs"} className={"uppercase font-mono"}>
              {t("core:two_factor_setup_authenticator_app")}
            </Text>
            <Text size={"sm"} className={"text-secondary"}>
              {t("core:two_factor_scan_qr")}
            </Text>
          </Stack>

          <Stack items={"center"} justify={"center"}>
            {twoFactorSetup.isPending ? (
              <Skeleton className={"aspect-square w-64 block"} />
            ) : (
              <img
                src={setupData?.qr_code_base64}
                className={"aspect-square w-64 block"}
                alt={""}
              />
            )}
          </Stack>

          <Stack gap={"lg"}>
            <Box>
              <Button
                type={"button"}
                variant="secondary"
                onClick={() => setShowManual((prev) => !prev)}
                rightIcon={
                  <FontAwesomeIcon
                    icon={`fa-light ${showManual ? "fa-chevron-up" : "fa-chevron-down"}`}
                  />
                }
              >
                <Text size={"md"} className={"font-semibold"}>
                  {t("core:two_factor_cant_scan_qr")}
                </Text>
              </Button>
            </Box>
            {showManual && (
              <Stack gap={"lg"}>
                <Stack>
                  <Text size={"xs"} className={"uppercase font-mono text-secondary"}>
                    {t("core:manual_setup")}
                  </Text>
                  <ListItem
                    preTitle={t("core:code_for_connection")}
                    title={setupData?.secret}
                    titleSlot={
                      twoFactorSetup.isPending ? <Skeleton className={"w-32 h-5"} /> : undefined
                    }
                    valueSlot={
                      <ButtonIcon
                        variant={"plain"}
                        icon={<FontAwesomeIcon icon={"fa-copy fa-light"} />}
                        onClick={() => {
                          if (setupData?.secret) {
                            copyTextWithNotify(setupData.secret);
                          }
                        }}
                      />
                    }
                  />
                </Stack>
                <Stack>
                  <Text size={"xs"} className={"uppercase font-mono text-secondary"}>
                    {t("core:recovery_codes")}
                  </Text>
                  <ListItem
                    preTitle={t("core:codes_for_backup")}
                    title={setupData?.backup_codes.join(",")}
                    titleSlot={
                      twoFactorSetup.isPending ? <Skeleton className={"w-32 h-5"} /> : undefined
                    }
                    valueSlot={
                      <ButtonIcon
                        variant={"plain"}
                        icon={<FontAwesomeIcon icon={"fa-copy fa-light"} />}
                        onClick={() => {
                          if (setupData?.backup_codes.join(",")) {
                            copyTextWithNotify(setupData.backup_codes.join(","));
                          }
                        }}
                      />
                    }
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>

        <form onSubmit={onSubmit}>
          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <CodeInput
                label={t("core:two_factor_enter_code")}
                caption={t("core:two_factor_from_authenticator")}
                value={field.value ?? ""}
                onChange={field.onChange}
                error={errors.code?.message}
                disabled={isChecking}
                onComplete={(_, fromPaste) => handleComplete(fromPaste)}
              />
            )}
          />

          <ServerErrorField errors={errors} />
        </form>
      </Stack>
    </Modal>
  );
}
